/**
 * Slice plane geometry.
 *
 * Pure maths with no VTK or DOM dependency: vtk-scene.js feeds the result to a
 * `vtkPlane`, and the mapping from a slider percentage to a world-space plane
 * stays testable on its own.
 */

/**
 * Plane normals for the slice axes, in the order the UI offers them.
 *
 * `vtkClipPolyData` keeps the region where its clip function is positive, which
 * for a `vtkPlane` is the side the normal points at. With these normals a slice
 * at 0% keeps the whole mesh and advancing the plane cuts material away.
 */
export const SLICE_NORMALS = {
  x: [1, 0, 0],
  y: [0, 1, 0],
  z: [0, 0, 1],
};

/**
 * Where the slice plane sits, as a world-space point.
 *
 * `percent` runs across the chosen axis's extent: 0 lands on that axis's
 * minimum bound, 100 on its maximum. The other two coordinates are centred, so
 * the origin stays inside the mesh wherever the mesh happens to be in space.
 *
 * @param {number[]} bounds VTK order: [xMin, xMax, yMin, yMax, zMin, zMax]
 * @param {'x'|'y'|'z'} axis
 * @param {number} percent 0-100; values outside the range are clamped
 * @returns {[number, number, number]}
 */
export function slicePlaneOrigin(bounds, axis, percent) {
  const sliced = Object.keys(SLICE_NORMALS).indexOf(axis);
  if (sliced < 0) {
    throw new RangeError(`Unknown slice axis "${axis}"`);
  }

  const fraction = Math.min(Math.max(Number(percent), 0), 100) / 100;
  const origin = [0, 0, 0];
  for (let index = 0; index < 3; index += 1) {
    const low = bounds[index * 2];
    const high = bounds[index * 2 + 1];
    origin[index] = index === sliced ? low + (high - low) * fraction : (low + high) / 2;
  }
  return origin;
}
